import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { terrainAdminApi } from '@/api/terrain-admin';
import { toast } from '@/stores/toastStore';

/**
 * SIMG-TERRAIN-2026-001 — Hooks TanStack Query pour le back-office terrain.
 * Toutes les mutations invalident les queries pertinentes pour un refetch auto.
 */

// ─── Query keys (tuples module-scope) ───────────────────────────

const K_CONFIG        = ['terrain', 'config'] as const;
const K_INTERVENANTS  = ['terrain', 'intervenants'] as const;
const K_ACCES         = ['terrain', 'acces'] as const;
const K_JOURNAL       = ['terrain', 'journal'] as const;
const K_SITES         = ['terrain', 'sites'] as const;
const K_AIRES         = ['terrain', 'aires'] as const;
const K_DECL_SOUMISES = ['terrain', 'declarations', 'soumises'] as const;

// Aujourd'hui hardcodé — quand une seconde filiale utilisera le portail terrain,
// remonter au niveau de la page (déduit du useAuthStore.filiale).
const FILIALE_DEFAUT = 'leadbetonCI';

// ─── Config ─────────────────────────────────────────────────────

export const useTerrainConfig = (filiale = FILIALE_DEFAUT) =>
  useQuery({
    queryKey: [...K_CONFIG, filiale] as const,
    queryFn:  () => terrainAdminApi.getConfig(filiale),
    staleTime: 30_000,
  });

export const useUpdateTerrainConfig = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: terrainAdminApi.updateConfig,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_CONFIG });
      toast.success('Configuration enregistrée', data.contactTel ?? '');
    },
  });
};

// ─── Intervenants ───────────────────────────────────────────────

export const useIntervenants = (opts?: { siteId?: string; actif?: boolean }) =>
  useQuery({
    queryKey: [...K_INTERVENANTS, opts?.siteId ?? null, opts?.actif ?? null] as const,
    queryFn:  () => terrainAdminApi.listIntervenants(opts),
    staleTime: 30_000,
  });

export const useCreateIntervenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: terrainAdminApi.createIntervenant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_INTERVENANTS });
      qc.invalidateQueries({ queryKey: K_ACCES });
      toast.success('Intervenant créé');
    },
  });
};

export const useUpdateIntervenant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: Parameters<typeof terrainAdminApi.updateIntervenant>[1] }) =>
      terrainAdminApi.updateIntervenant(args.id, args.patch),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: K_INTERVENANTS });
      // Désactiver révoque l'accès en cascade côté serveur
      if (args.patch.actif === false) qc.invalidateQueries({ queryKey: K_ACCES });
      toast.success('Intervenant mis à jour');
    },
  });
};

// ─── Accès ──────────────────────────────────────────────────────

export const useAcces = (opts?: { intervenantId?: string; actif?: boolean }) =>
  useQuery({
    queryKey: [...K_ACCES, opts?.intervenantId ?? null, opts?.actif ?? null] as const,
    queryFn:  () => terrainAdminApi.listAcces(opts),
    staleTime: 30_000,
  });

export const useGenerateAcces = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: terrainAdminApi.generateAcces,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_INTERVENANTS });
      qc.invalidateQueries({ queryKey: K_ACCES });
      // Pas de toast : la modale QR affiche déjà le succès de manière visible
    },
  });
};

export const useRevokeAcces = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; motif: string }) =>
      terrainAdminApi.revokeAcces(args.id, args.motif),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_INTERVENANTS });
      qc.invalidateQueries({ queryKey: K_ACCES });
      toast.success('Accès révoqué');
    },
  });
};

export const useJournalAcces = (opts?: { limit?: number; offset?: number; resultat?: string; depuis?: string }) =>
  useQuery({
    queryKey: [...K_JOURNAL, opts?.limit ?? null, opts?.offset ?? null, opts?.resultat ?? null, opts?.depuis ?? null] as const,
    queryFn:  () => terrainAdminApi.listJournal(opts),
    staleTime: 15_000,
  });

// ─── Sites ──────────────────────────────────────────────────────

export const useSites = () =>
  useQuery({
    queryKey: K_SITES,
    queryFn:  () => terrainAdminApi.listSites(),
    staleTime: 60_000,
  });

export const useCreateSite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: terrainAdminApi.createSite,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_SITES });
      toast.success('Site créé');
    },
  });
};

export const useUpdateSite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: Parameters<typeof terrainAdminApi.updateSite>[1] }) =>
      terrainAdminApi.updateSite(args.id, args.patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_SITES });
      toast.success('Site mis à jour');
    },
  });
};

// ─── Aires stockage ────────────────────────────────────────────

export const useAires = (siteId?: string) =>
  useQuery({
    queryKey: [...K_AIRES, siteId ?? null] as const,
    queryFn:  () => terrainAdminApi.listAires(siteId),
    staleTime: 60_000,
  });

export const useCreateAire = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: terrainAdminApi.createAire,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_AIRES });
      toast.success('Aire de stockage créée');
    },
  });
};

export const useUpdateAire = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; patch: Parameters<typeof terrainAdminApi.updateAire>[1] }) =>
      terrainAdminApi.updateAire(args.id, args.patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_AIRES });
      toast.success('Capacité mise à jour');
    },
  });
};

// ─── Déclarations à valider ────────────────────────────────────

export const useDeclarationsSoumises = () =>
  useQuery({
    queryKey: K_DECL_SOUMISES,
    queryFn:  () => terrainAdminApi.listDeclarationsSoumises(),
    staleTime: 15_000,
    refetchInterval: 60_000, // rafraîchissement doux si la page reste ouverte
  });

export const useValiderDeclaration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: terrainAdminApi.validerDeclaration,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_DECL_SOUMISES });
      toast.success('Déclaration validée');
    },
  });
};

export const useRenvoyerDeclaration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; motif: string }) =>
      terrainAdminApi.renvoyerDeclaration(args.id, args.motif),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K_DECL_SOUMISES });
      toast.success('Déclaration renvoyée pour correction');
    },
  });
};
